import React from "react";
import {
  CheckCircle,
  Paid,
  AccessTime,
  Info,
  ArrowForward,
  LocalShipping,
  CalendarToday
} from "@mui/icons-material";

/* ================= Types ================= */
interface CityOption {
  city: { id: number; name: string };
  delivery_fee: number;
  estimated_days: number;
}

interface ProviderInfo {
  name: string;
  rating: number;
  deliveries: number;
}

interface Props {
  selectedCity: CityOption;
  providerInfo?: ProviderInfo;
  showProviderInfo?: boolean;
  isRTL: boolean;
  t: (key: string) => string;
  currencySymbol?: string;
  onToggleProviderInfo?: () => void;
}

/* ================= Component ================= */
const ResponsiveDeliveryDetailsCard: React.FC<Props> = ({
  selectedCity,
  providerInfo,
  showProviderInfo = false,
  isRTL,
  t,
  currencySymbol = "ج.م",
  onToggleProviderInfo
}) => {
  const provider = providerInfo || {
    name: "Speed Express",
    rating: 4.8,
    deliveries: 1250
  };

  /* ================= Mobile (Compact) ================= */
  const CompactView = () => (
    <div className="bg-white rounded-xl p-4 border border-emerald-100">
      <div className={`flex items-center gap-3 ${isRTL ? "flex-row-reverse" : ""}`}>
        <div className="p-2 bg-emerald-500 rounded-lg">
          <CheckCircle className="text-white text-lg" />
        </div>
        <div>
          <h4 className="font-bold text-sm">{selectedCity.city.name}</h4>
          <p className="text-xs text-emerald-600">
            {t("cart.destinationConfirmed")}
          </p>
        </div>
      </div>

      <div className="mt-3 space-y-2 text-xs">
        <div className="flex justify-between">
          <span>{t("cart.deliveryFee")}</span>
          <b>{selectedCity.delivery_fee} {currencySymbol}</b>
        </div>
        <div className="flex justify-between">
          <span>{t("cart.deliveryTime")}</span>
          <b>{selectedCity.estimated_days} {t("cart.days")}</b>
        </div>
      </div>
    </div>
  );

  /* ================= Desktop / Tablet (Premium) ================= */
  const PremiumView = () => (
    <div className="bg-gradient-to-r from-white to-gray-50 rounded-2xl p-6 border border-emerald-100 shadow-lg">
      {/* Header */}
      <div className={`flex gap-4 ${isRTL ? "flex-row-reverse" : ""}`}>
        <div className="p-3 bg-emerald-500 rounded-xl">
          <CheckCircle className="text-white text-2xl" />
        </div>
        <div>
          <h4 className="text-lg font-bold">{selectedCity.city.name}</h4>
          <p className="text-sm text-emerald-600">
            {t("cart.destinationConfirmed")}
          </p>
        </div>
      </div>

      {/* Info Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
        <InfoCard
          icon={<Paid />}
          label={t("cart.deliveryFee")}
          value={`${selectedCity.delivery_fee} ${currencySymbol}`}
        />
        <InfoCard
          icon={<AccessTime />}
          label={t("cart.deliveryTime")}
          value={`${selectedCity.estimated_days} ${t("cart.days")}`}
        />
        <InfoCard
          icon={<CalendarToday />}
          label={t("cart.estimatedDelivery")}
          value={t("cart.soon")}
        />
      </div>

      {/* Provider */}
      {showProviderInfo && (
        <div className="mt-6 bg-blue-50 rounded-xl p-4 border border-blue-200">
          <h5 className="font-semibold text-blue-700 mb-3">
            {t("cart.providerInfo")}
          </h5>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div>🚚 {provider.name}</div>
            <div>⭐ {provider.rating}/5</div>
            <div>📦 {provider.deliveries.toLocaleString()}+</div>
            <div>🌍 {t("cart.nationwide")}</div>
          </div>
        </div>
      )}

      {!showProviderInfo && onToggleProviderInfo && (
        <button
          onClick={onToggleProviderInfo}
          className="mt-4 w-full py-3 bg-blue-50 border border-blue-200 rounded-xl text-blue-700 flex items-center justify-center gap-2"
        >
          <Info />
          {t("cart.viewProviderInfo")}
          <ArrowForward className={`${isRTL ? "rotate-180" : ""}`} />
        </button>
      )}
    </div>
  );

  /* ================= Render ================= */
  return (
    <>
      {/* Mobile */}
      <div className="block sm:hidden">
        <CompactView />
      </div>

      {/* Tablet + Desktop */}
      <div className="hidden sm:block">
        <PremiumView />
      </div>
    </>
  );
};

/* ================= Helper ================= */
const InfoCard = ({
  icon,
  label,
  value
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) => (
  <div className="bg-white p-4 rounded-xl border">
    <div className="flex items-center gap-2 text-gray-600">
      {icon}
      <span className="text-xs">{label}</span>
    </div>
    <div className="mt-1 font-bold text-gray-900">{value}</div>
  </div>
);

export default ResponsiveDeliveryDetailsCard;
